import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format } from 'date-fns';
import { Users, ArrowRight, Check, MapPin, Filter, X } from 'lucide-react';
import SubscriptionUpsell from './SubscriptionUpsell';
import FloorPlanSVG from './FloorPlanSVG';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STOCK_IMAGES = [
  'https://images.pexels.com/photos/9301900/pexels-photo-9301900.jpeg?auto=compress&w=600',
  'https://images.unsplash.com/photo-1505624198937-c704aff72608?w=600&q=80',
  'https://images.pexels.com/photos/20101490/pexels-photo-20101490.jpeg?auto=compress&w=600',
  'https://images.unsplash.com/photo-1650521986932-86bbeded3fc2?w=600&q=80',
  'https://images.pexels.com/photos/2505056/pexels-photo-2505056.jpeg?auto=compress&w=600',
];

/**
 * Step 1 content: choose an space (with all 4 prices) + compact subscription upsell.
 * The stepper itself lives one level up (PlanStepper).
 * Props:
 *  - selectedSpace: string | null  (controlled)
 *  - onSelectSpace: (id) => void
 *  - onNext: () => void   (advances to step 2)
 */
export default function HomeSpacesBlocks({ selectedSpace, onSelectSpace, onNext }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [spaces, setSpaces] = useState([]);
  const [plans, setPlans] = useState([]);
  // ---- Slot-availability filter (colours the interactive plan) ----
  // Default date = today (local ISO), no time picked so the plan stays
  // fully "available" until the user narrows the window.
  const today = format(new Date(), 'yyyy-MM-dd');
  const [slotDate, setSlotDate] = useState(today);
  const [slotStart, setSlotStart] = useState('');
  const [slotEnd, setSlotEnd] = useState('');
  const [bookingsForDate, setBookingsForDate] = useState({}); // { space_id: [{start, end}] }

  useEffect(() => {
    (async () => {
      try {
        const [spacesRes, plansRes] = await Promise.all([
          axios.get(`${API}/spaces`),
          axios.get(`${API}/subscriptions/plans`).catch(() => ({ data: [] })),
        ]);
        setSpaces(spacesRes.data);
        setPlans(plansRes.data);
      } catch { /* ignore */ }
    })();
  }, []);

  // Fetch bookings for the chosen date only when a filter is set.
  useEffect(() => {
    if (!slotDate) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/availability/range`, {
          params: { start_date: slotDate, end_date: slotDate },
        });
        if (cancelled) return;
        setBookingsForDate(res.data?.[slotDate] || {});
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [slotDate]);

  // Compute which spaces overlap the chosen [slotStart, slotEnd] window.
  // If no window is picked, nothing is "booked" from the filter's POV —
  // the plan colours reflect availability only when the user narrows.
  const bookedSpaces = useMemo(() => {
    if (!slotStart || !slotEnd || slotStart >= slotEnd) return [];
    const overlaps = (a1, a2, b1, b2) => a1 < b2 && b1 < a2;
    const set = new Set();
    Object.entries(bookingsForDate).forEach(([spaceId, slots]) => {
      const anyOverlap = slots.some((s) => overlaps(slotStart, slotEnd, s.start_time, s.end_time));
      if (anyOverlap) set.add(spaceId);
    });
    return Array.from(set);
  }, [bookingsForDate, slotStart, slotEnd]);

  const filterActive = !!(slotStart && slotEnd && slotStart < slotEnd);
  const clearFilter = () => { setSlotStart(''); setSlotEnd(''); };

  // Half-hour slots between 06:00 and 22:00 for the filter dropdowns.
  const TIME_SLOTS = useMemo(() => {
    const arr = [];
    for (let h = 6; h <= 22; h++) {
      arr.push(`${String(h).padStart(2, '0')}:00`);
      if (h < 22) arr.push(`${String(h).padStart(2, '0')}:30`);
    }
    return arr;
  }, []);

  return (
    <section className="py-10 sm:py-14 bg-white" data-testid="home-spaces-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C77D63]/10 border border-[#C77D63]/25 rounded-full text-[11px] font-bold uppercase tracking-wider text-[#C77D63] mb-3">
            {t('landing.step', 'Étape')} 1
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-black tracking-tight">
            {t('spaces.chooseSpaceTitle', 'Choisissez votre espace')}
          </h2>
          <p className="mt-2 text-base text-black/70">
            {t('spaces.chooseSpaceSubtitle', "Cliquez sur l'espace qui correspond à votre activité, puis passez au choix de l'horaire.")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
          {spaces.map((space, i) => {
            const isSelected = selectedSpace === space.space_id;
            return (
              <button
                type="button"
                key={space.space_id}
                data-testid={`space-card-${space.space_id}`}
                onClick={() => onSelectSpace?.(space.space_id)}
                className={`text-left bg-white rounded-xl border overflow-hidden group transition-all duration-300 ${
                  isSelected
                    ? 'border-[#C77D63] ring-2 ring-[#C77D63] shadow-lg -translate-y-1'
                    : 'border-[#E2E2DF] hover:shadow-md hover:-translate-y-0.5'
                }`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#F0EFEA]">
                  <img
                    src={space.image_url || STOCK_IMAGES[i % STOCK_IMAGES.length]}
                    alt={space.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {isSelected && (
                    <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#C77D63] text-white flex items-center justify-center shadow-md">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-[#C77D63] bg-[#C77D63]/10 px-1.5 py-0.5 rounded">
                      {space.space_type || 'Espace'}
                    </span>
                    <span className="text-[9px] text-[#737373] flex items-center gap-0.5">
                      <Users className="w-2.5 h-2.5" /> {space.capacity}
                    </span>
                  </div>
                  <h3 className={`font-heading text-sm font-bold leading-tight line-clamp-1 ${isSelected ? 'text-[#C77D63]' : 'text-black'}`}>
                    {space.name || t(`spaces.names.${space.space_id}`, { defaultValue: space.space_id })}
                  </h3>
                  <p className="mt-1 text-[11px] text-black/70 leading-snug line-clamp-2 min-h-[28px]">
                    {t(`spaces.descriptions.${space.space_id}`, { defaultValue: space.description || t('spaces.defaultDesc', { defaultValue: 'Espace équipé et flexible' }) })}
                  </p>
                  <div className="mt-2.5 pt-2.5 border-t border-[#F0EFEA] space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className="text-[10px] text-[#737373]">{t('spaces.perHourLabel', 'Heure')}</span>
                      <span className="font-mono font-semibold text-[13px] text-black">{space.price_hourly} €</span>
                    </div>
                    {space.price_half_day > 0 && (
                      <div className="flex justify-between items-baseline">
                        <span className="text-[10px] text-[#737373]">{t('spaces.perHalfDayLabel', '½ journée · 4h')}</span>
                        <span className="font-mono font-semibold text-[13px] text-black">{space.price_half_day} €</span>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline">
                      <span className="text-[10px] text-[#737373]">{t('spaces.perDayLabel', 'Journée')}</span>
                      <span className="font-mono font-semibold text-[13px] text-black">{space.price_daily} €</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-[10px] text-[#737373]">{t('spaces.perWeekLabel', 'Semaine')}</span>
                      <span className="font-mono font-semibold text-[13px] text-black">{space.price_weekly} €</span>
                    </div>
                  </div>
                  <div className="mt-1.5 text-[9px] text-[#C77D63] font-semibold text-center">
                    {t('spaces.htvaNoticeCompact', 'HTVA · TVA 21% en sus')}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {plans.length > 0 && (
          <div className="mt-8">
            <SubscriptionUpsell plans={plans} />
          </div>
        )}

        <div className="mt-10 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={onNext}
            disabled={!selectedSpace}
            data-testid="cta-choose-time"
            className="bg-[#C77D63] text-white hover:bg-[#b06a51] disabled:bg-[#E2E2DF] disabled:text-[#B7B7B0] disabled:cursor-not-allowed transition-all rounded-xl font-bold px-10 py-5 text-base shadow-lg shadow-[#C77D63]/25 inline-flex items-center gap-3"
          >
            {t('spaces.chooseTimeCta', "Choisissez l'horaire")}
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-xs text-[#737373]">
            {selectedSpace
              ? t('spaces.selectedHint', 'Passez à l\'étape 2 : sélectionnez la date et le créneau.')
              : t('spaces.pickOneHint', "Astuce : cliquez d'abord sur un espace ci-dessus.")}
          </p>
        </div>

        {/* Interactive floor plan — kept in the DOM but hidden by default so
            the "Les Espaces" step stays focused on the card list. Flip the
            `hidden` class to re-enable, or wire a toggle button later. */}
        <div className="hidden mt-10 bg-white border border-[#E2E2DF] rounded-xl p-5 shadow-sm" data-testid="step1-floor-plan-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-heading text-lg font-bold text-[#1A3C34] flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {t('booking.floorPlanTitle', { defaultValue: 'Plan interactif' })}
            </h3>
            <span className="text-xs text-[#737373]">
              {t('booking.floorPlanHintPick', { defaultValue: 'Cliquez sur un espace pour le sélectionner' })}
            </span>
          </div>

          {/* Slot-availability filter — colours the plan in real time */}
          <div
            className="mb-4 bg-[#F9F9F7] border border-[#E2E2DF] rounded-lg p-3"
            data-testid="floor-plan-slot-filter"
          >
            <div className="flex items-center gap-2 mb-2">
              <Filter className="w-4 h-4 text-[#C77D63]" />
              <span className="text-sm font-bold text-[#1A3C34]">
                {t('booking.slotFilterTitle', { defaultValue: 'Voir les disponibilités par créneau' })}
              </span>
              {filterActive && (
                <button
                  type="button"
                  onClick={clearFilter}
                  data-testid="slot-filter-clear"
                  className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-[#737373] hover:text-[#1A3C34]"
                >
                  <X className="w-3 h-3" /> {t('booking.clear', { defaultValue: 'Effacer' })}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                type="date"
                value={slotDate}
                min={today}
                onChange={(e) => setSlotDate(e.target.value)}
                data-testid="slot-filter-date"
                className="h-10 border border-[#E2E2DF] rounded-md px-3 text-sm font-medium text-[#1A3C34] bg-white"
              />
              <select
                value={slotStart}
                onChange={(e) => setSlotStart(e.target.value)}
                data-testid="slot-filter-start"
                className="h-10 border border-[#E2E2DF] rounded-md px-3 text-sm font-medium text-[#1A3C34] bg-white"
              >
                <option value="">{t('booking.startHour', { defaultValue: 'De…' })}</option>
                {TIME_SLOTS.slice(0, -1).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={slotEnd}
                onChange={(e) => setSlotEnd(e.target.value)}
                disabled={!slotStart}
                data-testid="slot-filter-end"
                className="h-10 border border-[#E2E2DF] rounded-md px-3 text-sm font-medium text-[#1A3C34] bg-white disabled:bg-[#F0EFEA] disabled:text-[#B7B7B0]"
              >
                <option value="">{t('booking.endHour', { defaultValue: 'À…' })}</option>
                {TIME_SLOTS.filter((s) => s > slotStart).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            {filterActive && (
              <p className="mt-2 text-[11px] text-[#737373]" data-testid="slot-filter-summary">
                {bookedSpaces.length === 0
                  ? t('booking.slotFilterAllFree', {
                      defaultValue: 'Tous les espaces sont libres sur ce créneau ✓',
                    })
                  : t('booking.slotFilterSomeBooked', {
                      defaultValue: '{{count}} espace(s) déjà réservé(s) sur ce créneau — grisés sur le plan.',
                      count: bookedSpaces.length,
                    })}
              </p>
            )}
          </div>

          <FloorPlanSVG
            selectedSpace={selectedSpace}
            onSelectSpace={(id) => onSelectSpace(id)}
            bookedSpaces={bookedSpaces}
            spacePrices={
              spaces.reduce((acc, s) => ({
                ...acc,
                [s.space_id]: { hourly: s.hourly },
              }), {})
            }
          />
        </div>
      </div>
    </section>
  );
}
